import { inject, Injectable, signal } from '@angular/core'
import { debounceTime, distinctUntilChanged, map, take, tap } from 'rxjs'
import { APIRequestResources } from '../enums'
import { IAm, ResourceResponse } from '../interfaces'
import { Action, APIRequestResource, Resource } from '../types'
import { ttlToMilliseconds } from '../util'
import { ApiService } from './api.service'

@Injectable({
  providedIn: 'root',
})
export class IAmService {
  private readonly apiService = inject(ApiService)

  private readonly accessGroups = {
    create: ['create', 'manage'],
    read: ['read', 'access', 'manage'],
    update: ['update', 'modify', 'access', 'manage'],
    delete: ['delete', 'modify', 'access', 'manage'],
    modify: ['modify', 'access', 'manage'],
    access: ['access', 'manage'],
    manage: ['manage'],
  }
  private readonly resource: APIRequestResource = APIRequestResources.IdentityService

  readonly $$active = signal(undefined as IAm | undefined)

  whoAmI() {
    return this.apiService
      .GET<IAm>(this.resource, { endpoint: 'auth/me' }, false, false, ttlToMilliseconds('5m'))
      .pipe(tap((res) => this.$$active.set(res.data)))
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.apiService.PATCH<any>(
      this.resource,
      { current_password: currentPassword, new_password: newPassword },
      { endpoint: 'auth/me/passwords' }
    )
  }

  getPermissions(resourcePaths: string) {
    return this.apiService.GET<ResourceResponse[]>(
      this.resource,
      { endpoint: 'auth/me/resources', params: { resource_paths: resourcePaths } },
      true,
      false,
      ttlToMilliseconds('1m')
    )
  }

  checkPermission(resource: Resource, actions: Action[], condition: 'or' | 'and' = 'and', type: 'full' | 'all' = 'all') {
    return this.getPermissions(resource).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      take(1),
      map((res) => res.data),
      map((resources) => {
        if (!resources) return false

        return condition === 'and'
          ? resources
              .map((res) =>
                res?.permissions?.some(
                  (permission) =>
                    (type === 'full' ? permission.type === 'full' : true) &&
                    actions
                      .map((action) => this.accessGroups[action].includes(permission?.action ?? 'no-action'))
                      .every((entry) => entry === true)
                )
              )
              .includes(true)
          : resources
              .map((res) =>
                res?.permissions?.some(
                  (permission) =>
                    (type === 'full' ? permission.type === 'full' : true) &&
                    actions
                      .map((action) => this.accessGroups[action].includes(permission?.action ?? 'no-action'))
                      .some((entry) => entry === true)
                )
              )
              .includes(true)
      })
    )
  }

  checkLimit(resource: Resource, field: string, type: 'full' | 'all' = 'all') {
    return this.getPermissions(resource).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      take(1),
      map((res) => res.data),
      map((resources) => {
        const limitResources = resources?.[0].permissions?.filter(
          (permission) => permission.action === 'limit' && permission.field === field && (permission.type === type || type === 'all')
        )

        if (!resources) return 0
        if (!limitResources?.length) return 0

        return limitResources[0].limitation
      })
    )
  }
}
